import { useEffect } from 'react'
import { useOutletContext } from 'react-router-dom'
import { useSignal } from '@preact/signals-react'

import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'
import Table from 'react-bootstrap/Table'

import Loadable from '../../../../components/Loadable.jsx'
import SelectParentBusinessEntityLeaf from '../../components/SelectParentBusinessEntityLeaf.jsx'

import { NotificationTypes, addNotification } from '../../../../state/actions/notificationActions.js'
import { fetchCanGrantRolesList } from '../../../../state/actions/roleActions.js'
import { addUserRole } from '../../../../state/actions/userActions.js'

import { canGrantRoleListSignal, canGrantRoleListLoadedSignal, canGrantRoleListErrorSignal } from '../../../../state/signals/roleSignals.js'

import { scrollToTop } from '../../../../utils/utils.js'

function AddRolesModal({ showSignal, cancelAdd, businessEntity, userId }) {

    const { user, config, accessToken, userPermissions, userPermissionsSummary, accordionData: sidebarAccordionData } = useOutletContext()

    useEffect(() => {
        fetchCanGrantRolesList(businessEntity.id, config, accessToken)
    }, [businessEntity.id, config, accessToken])

    const selectedRoleSignal = useSignal('')

    const enableSubmitSignal = useSignal(false)

    const selectedBusinessEntitiesSignal = useSignal(new Set())

    function handleSelectRole(roleId) {
        //console.log("Selected role ID: ", roleId)
        selectedRoleSignal.value = roleId
    }

    function resetForm() {
        selectedBusinessEntitiesSignal.value = new Set()
        enableSubmitSignal.value = false
        selectedRoleSignal.value = ''
    }

    function handleAddUserRole() {
        const requestBody = [...selectedBusinessEntitiesSignal.value.values().toArray()]
        addUserRole(businessEntity.id, userId, selectedRoleSignal.value, requestBody, config, accessToken).then(
            () => {
                resetForm()
                scrollToTop("main")
                addNotification(`Successfully added role to user`, NotificationTypes.SUCCESS)
            }
        ).catch(
            (error) => {
                scrollToTop("main")
                addNotification(`Failed to add role to user: ${error.toString()}`, NotificationTypes.ERROR)
            }
        )
        cancelAdd()
    }

    function getAllBeIds(be) {
        const ids = [be.id]
        for (const company of be.companies) {
            ids.push(...getAllBeIds(company))
        }
        return ids
    }

    function handleSelectAll() {
        const allIds = getAllBeIds(businessEntity)
        const allSelected = allIds.every(id => selectedBusinessEntitiesSignal.value.has(id))
        if (allSelected) {
            selectedBusinessEntitiesSignal.value = new Set()
            enableSubmitSignal.value = false
        } else {
            selectedBusinessEntitiesSignal.value = new Set(allIds)
            enableSubmitSignal.value = true
        }
    }

    function RolesOptions({ items }) {
        if (!items || !items.length)
            return <option>No Roles to Display</option>

        let rows = []
        items.map((item, idx) => (
            rows.push(<option value={item.id} key={idx}>{item.name}</option>)
        ))
        return rows
    }

    return (
        <Modal
            show={showSignal.value}
            onHide={cancelAdd}
            backdrop="static"
            size="xl"
        >
            <Modal.Header closeButton className="panel-heading">
                <Modal.Title className="panel-title">Add Role</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group as={Form.Row} className="mb-3" controlId="formGroupRoles">
                        <Form.Label>Choose Role</Form.Label>
                        <Loadable loadedSignals={[canGrantRoleListLoadedSignal]} errorSignals={[canGrantRoleListErrorSignal]}>
                            <Form.Select
                                onChange={(e) => { handleSelectRole(e.target.value) }}
                                value={selectedRoleSignal.value}
                            >
                                <option value="-1">Select a Role to Add</option>
                                <RolesOptions items={canGrantRoleListSignal.value} />
                            </Form.Select>
                        </Loadable>
                    </Form.Group>
                    <Form.Group as={Form.Row} className="mb-3" controlId="formGroupAccounts">
                        <div className="d-flex justify-content-between align-items-center">
                            <Form.Label className="mb-0">Choose Accounts</Form.Label>
                            {selectedRoleSignal.value && (
                                <Button variant="link" size="sm" className="p-0 mb-1" onClick={handleSelectAll}>
                                    {getAllBeIds(businessEntity).every(id => selectedBusinessEntitiesSignal.value.has(id)) ? 'Deselect All' : 'Select All'}
                                </Button>
                            )}
                        </div>
                        <div style={{maxHeight:"calc(100vh - 368px)", overflowY:"auto"}}>
                            <Table>
                                <tbody>
                                    {
                                        selectedRoleSignal.value ? (
                                            <SelectParentBusinessEntityLeaf
                                                businessEntity={businessEntity}
                                                type="checkbox"
                                                selectedBusinessEntityIdsSignal={selectedBusinessEntitiesSignal}
                                                enableSubmitSignal={enableSubmitSignal}
                                                depth={0}
                                            />
                                        ) : (
                                            <tr><td>To begin, please select a role from above.</td></tr>
                                        )
                                    }
                                </tbody>
                            </Table>
                        </div>
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer className="panel-footer col-xs-12">
                <div className='pull-right'>
                    <Button className="btn btn-md btn-default" style={{ marginRight: '1rem' }} onClick={cancelAdd}>Cancel</Button>
                    <Button className="btn btn-md btn-primary" onClick={handleAddUserRole} disabled={!enableSubmitSignal.value}>Add</Button>
                </div>
            </Modal.Footer>
        </Modal>
    )
}

export default AddRolesModal